import React, {
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormField as WdsFormField,
  Loader,
  OTPInput,
  OTPInputImperativeActions,
} from '@wix/design-system';
import {
  GenericEventsBiLoggerProvider,
  PageContextProvider,
} from '@wix/data-tools-generic-events';
import { AppContextProvider } from '../AppLoader';
import { AuthDivider, AuthPage, BodyContainer } from '../AuthPage/AuthPage';
import {
  BEHAVIORS,
  ButtonWithLoader,
} from '../ButtonWithLoader/ButtonWithLoader';
import { DialogFormContainer } from '../DialogContainer';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { ThemedText, ThemedTextButton } from '../ThemedComponents';
import { dataHooks } from '../../dataHooks';
import type { EmailCodeStore } from '../../stores/emailCodeStore';
import s from './EmailCodeDialog.scss';

const CODE_INPUT_ID = 'email-code';

/** What the code field reads from and writes to; both form-field stores fit. */
export interface CodeFieldModel {
  value: string;
  isDirty: boolean;
  isTouched: boolean;
  serverError: ReactNode;
  errorInfo: ReactNode;
  setValue(value: string): void;
  triggerValidator?(): void;
  onFocus(): void;
  onBlur(): void;
}

/**
 * The emailed code, one slot per digit. It is submitted as soon as its last
 * digit is typed, pasted or autofilled, or with Enter. Like the other inputs,
 * its error waits while the user is still typing: it shows after a submit or
 * a blur.
 */
export const CodeField: React.FC<{
  field: CodeFieldModel;
  label: string;
  autoFocus: boolean;
  /** While a code is verified or sent, so the code can't change meanwhile. */
  readOnly: boolean;
  inputRef: RefObject<OTPInputImperativeActions>;
  onSubmit: () => void;
  /** Shows "Verifying" under the slots, for screens without a submit button. */
  isVerifying?: boolean;
  /** Re-runs the validator on each change, for fields that don't on their own. */
  revalidateOnChange?: boolean;
  id?: string;
  dataHook?: string;
}> = observer(
  ({
    field,
    label,
    autoFocus,
    readOnly,
    inputRef,
    onSubmit,
    isVerifying,
    revalidateOnChange,
    id = CODE_INPUT_ID,
    dataHook = dataHooks.enterCode.codeInput,
  }) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);

    const errorText = field.serverError || field.errorInfo;
    const showError =
      field.isDirty && field.isTouched && Boolean(errorText) && !isEditing;

    const submit = () => {
      setIsEditing(false);
      onSubmit();
    };

    return (
      // OTPInput has no Enter handler of its own; its input's keydown bubbles here.
      <div
        className={s.codeField}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            submit();
          }
        }}
      >
        <WdsFormField
          id={id}
          label={label}
          status={isVerifying ? 'loading' : showError ? 'error' : undefined}
          statusMessage={
            isVerifying ? (
              <Box gap="6px" verticalAlign="middle">
                <Loader size="tiny" />
                {t('verifying_button')}
              </Box>
            ) : showError ? (
              errorText
            ) : undefined
          }
        >
          <OTPInput
            ref={inputRef}
            id={id}
            size="large"
            autoFocus={autoFocus}
            readOnly={readOnly}
            value={field.value}
            onChange={(value) => {
              runInAction(() => {
                field.setValue(value);
                // Otherwise a rejected code's error stays and blocks the next attempt.
                if (revalidateOnChange) {
                  field.triggerValidator?.();
                }
              });
              setIsEditing(true);
            }}
            onComplete={submit}
            onFocus={field.onFocus}
            onBlur={() => {
              field.onBlur();
              setIsEditing(false);
            }}
            dataHook={dataHook}
          />
        </WdsFormField>
        {/* The FormField message isn't announced when it appears, and a code
          sent by its last digit leaves the focus where it was; read it out. */}
        <span role="alert" className={s.visuallyHidden}>
          {showError ? errorText : ''}
        </span>
      </div>
    );
  },
);

/** Seconds until `availableAt` (epoch ms), ticking once a second. */
const useSecondsUntil = (availableAt: number) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (availableAt <= Date.now()) {
      return undefined;
    }
    const interval = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= availableAt) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [availableAt]);
  return Math.max(0, Math.ceil((availableAt - now) / 1000));
};

const ResendCode: React.FC<{ store: EmailCodeStore }> = observer(
  ({ store }) => {
    const { t } = useTranslation();
    const secondsLeft = useSecondsUntil(store.resendAvailableAt);
    if (secondsLeft > 0) {
      return (
        <ThemedText
          size="small"
          secondary
          dataHook={dataHooks.enterCode.resendButton}
        >
          {t('signup.email_code.resend_in', {
            defaultValue: 'Resend Code in {seconds}s',
            seconds: secondsLeft,
          })}
        </ThemedText>
      );
    }
    return (
      <Box>
        <ThemedTextButton
          size="small"
          underline="always"
          disabled={store.isLoading}
          onClick={() => store.resendCode()}
          dataHook={dataHooks.enterCode.resendButton}
        >
          {t('signup.email_code.resend', { defaultValue: 'Resend Code' })}
        </ThemedTextButton>
      </Box>
    );
  },
);

/** The "Check your email" step shared by email-code signup and login. */
export const EmailCodeDialog: React.FC<{
  store: EmailCodeStore;
  pageName: string;
  /** Prefix for the screen's own data hooks, e.g. `signup.emailCode`. */
  dataHookPrefix: string;
  submitLabel: string;
  /** The button that switches to the password form. */
  passwordLabel: string;
  /** A CAPTCHA a refused send asked for, under the resend link. */
  captcha?: ReactNode;
  /** Policies under the card, next to the reCAPTCHA notice that is always there. */
  belowCard?: ReactNode;
  /** Rendered at the bottom of the card, e.g. the policies. */
  children?: ReactNode;
  /**
   * true: the modal header's back goes to the email step, as the main
   * header's does. false: it goes back in history.
   */
  shouldGoBackToEmailInModal: boolean;
}> = observer((props) => {
  const {
    store,
    pageName,
    dataHookPrefix,
    submitLabel,
    passwordLabel,
    shouldGoBackToEmailInModal,
    captcha,
    belowCard,
    children,
  } = props;
  const { t } = useTranslation();
  const {
    rootStore: {
      displayStore: { isMobile, isVerticalLayout, isDialogCentered },
      biLogger,
      notificationStore,
    },
  } = useContext(AppContextProvider);

  useEffect(() => {
    notificationStore.close();
  }, []);

  const codeInput = useRef<OTPInputImperativeActions>(null);
  const submit = async () => {
    await store.verify({ code: store.codeField.value });
    // A rejected or incomplete code: back to the slots, with a full code's
    // first digit selected, so typing replaces it.
    if (!store.codeField.isValid) {
      codeInput.current?.focus();
    }
  };

  return (
    <GenericEventsBiLoggerProvider value={biLogger}>
      <PageContextProvider pageContext={{ pageName }}>
        <Box
          direction="vertical"
          width="100%"
          align="center"
          className={isDialogCentered ? 'dialogCenteredWrapper' : undefined}
        >
          <DialogFormContainer
            dataHook={dataHooks.enterCode.container}
            formClasses={[s.form]}
            autoCenter={false}
            mainHeaderProps={{
              showBackButton: true,
              backButtonCallback: store.backToEmail,
            }}
            shouldUseCallbackInModalHeader={shouldGoBackToEmailInModal}
          >
            <BodyContainer
              priority="secondary"
              withPolicies={Boolean(children)}
            >
              <DialogHeader>
                <DialogHeader.Title as="h1" flow="secondaryFlow">
                  {t('signup.email_code.title', {
                    defaultValue: 'Check your email',
                  })}
                </DialogHeader.Title>
                {/* One header child, so the email sits right under the line. */}
                <Box direction="vertical" align="center">
                  <DialogHeader.SubTitleSmall>
                    {t('signup.email_code.description', {
                      defaultValue: 'Enter the code sent to:',
                    })}
                  </DialogHeader.SubTitleSmall>
                  <DialogHeader.SubTitleSmall>
                    <Box gap={1} align="center" verticalAlign="middle">
                      <ThemedText weight="bold">
                        {/* Keeps its own direction in an RTL page. */}
                        <bdi>{store.email}</bdi>
                      </ThemedText>
                      <ThemedTextButton
                        skin="standard"
                        underline="none"
                        onClick={store.backToEmail}
                        dataHook={`${dataHookPrefix}.changeEmail`}
                      >
                        {t('signup.email_code.change_email', {
                          defaultValue: 'Change',
                        })}
                      </ThemedTextButton>
                    </Box>
                  </DialogHeader.SubTitleSmall>
                </Box>
              </DialogHeader>
              <Box
                direction="vertical"
                align={isVerticalLayout ? 'center' : 'left'}
                className={s.codeInput}
                gap={4}
              >
                <Box direction="vertical" width="100%" gap={1}>
                  {/* No gap above resend, like Forgot Password under the login password. */}
                  <Box direction="vertical" width="100%">
                    <CodeField
                      field={store.codeField}
                      label={t('enter_code.code_input_placeholder')}
                      autoFocus={!isMobile}
                      readOnly={store.isLoading}
                      inputRef={codeInput}
                      onSubmit={submit}
                    />
                    <Box width="100%" align="left" className={s.resend}>
                      <ResendCode store={store} />
                    </Box>
                  </Box>
                  {captcha}
                </Box>
                {/* The divider brings its own vertical padding. */}
                <Box direction="vertical" gap={1} width="100%">
                  <ButtonWithLoader
                    dataHook={dataHooks.enterCode.verifyButton}
                    size={undefined}
                    type="submit"
                    skin="standard"
                    priority="primary"
                    onClick={submit}
                    // Spins while a code is verified, however it was sent, and
                    // after a verified code until the page is left.
                    showLoader={store.isVerifying || store.isCompleting}
                    disabled={
                      store.isLoading &&
                      !store.isVerifying &&
                      !store.isCompleting
                    }
                    behaviour={BEHAVIORS.MULTI_CLICKS}
                  >
                    {submitLabel}
                  </ButtonWithLoader>
                  <AuthDivider
                    text={t('emailStep.divider')}
                    className={classNames({ [s.divider]: isVerticalLayout })}
                  />
                  <ButtonWithLoader
                    size={undefined}
                    priority="secondary"
                    className={s.outlinedButton}
                    disabled={store.isLoading}
                    onClick={store.switchToPassword}
                    dataHook={`${dataHookPrefix}.password`}
                  >
                    {passwordLabel}
                  </ButtonWithLoader>
                </Box>
              </Box>
              {children}
            </BodyContainer>
          </DialogFormContainer>
          <AuthPage.Policies>{belowCard}</AuthPage.Policies>
        </Box>
      </PageContextProvider>
    </GenericEventsBiLoggerProvider>
  );
});
